<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import IconExpenses from '@lahaus-roomie/roomie/src/assets/icons/expenses.svg'
import type { Props } from './types'
import { useAppChatStore } from '@/stores/appChatStore'

const props = defineProps<Props>()
const emit = defineEmits(['showCta', 'clickPersonalizePlanCta'])

const appChatStore = useAppChatStore()
const observableElementsVisibility = ref<{ [key: string]: boolean }>({})
const samPresentationSectionVisible = ref(false)

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isSomeObservableElementVisible = computed(() => Object.values(observableElementsVisibility.value).some(value => value))

const setFixedCtaObserver = () => {
  const typologiesFromObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => (observableElementsVisibility.value[entry.target.className] = entry.isIntersecting))
  }, { threshold: 0.1 })

  for (const selector of props.observableSelectors) {
    const observableElement = document.querySelector(selector) as HTMLDivElement
    if (observableElement) {
      observableElementsVisibility.value[observableElement.className] = false
      typologiesFromObserver.observe(observableElement)
    }
  }

  const samPresentationSectionObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => (samPresentationSectionVisible.value = entry.isIntersecting))
  }, { threshold: 0.1 })
  const samPresentationSection = document.querySelector('#sam-presentation-section') as HTMLDivElement
  if (samPresentationSection) samPresentationSectionObserver.observe(samPresentationSection)
}

const handleCtaClick = () => {
  const endpointConfig = {
    screen: 'PDP',
    entryNameAlias: 'PDP_CHAT_WEB_CONSTRUCTOR',
    startFlow: true,
    shouldCallBotForGreeting: true
  }
  appChatStore.openChatWindow(endpointConfig)
}

const showCta = computed(() => true)

watch(showCta, (newValue) => {
  emit('showCta', newValue)
})

onMounted(() => {
  setTimeout(() => setFixedCtaObserver())
})

const { t } = useI18n()

</script>

<script lang="ts">
export default {
  name: 'PdFixedCtas'
}
</script>

<template>
  <div
    class="pd-fixed-ctas fixed right-0 flex flex-col z-10 transition-all duration-500 ease-in-out"
    :style="{
      bottom: showCta ? '0' : '-100%'
    }">
    <div class="w-screen bg-white py-16 px-24 md:py-14 md:px-40 lg:px-56 flex flex-col md:flex-row justify-center items-center md:justify-between shadow-elevation-light-level-8">
      <div class="flex md:hidden items-center mb-12 lg:mb-8">
        <span class="flex h-32 w-32 items-center justify-center flex-none rounded-circle border border-brand-300 fill-carbon-600 pointer-events-none px-6 py-6">
          <IconExpenses class="w-full h-full" />
        </span>

        <p class="ml-8 text-primary-800 text-16 font-medium lg:text-16 !leading-8">
          {{ t('valueProposition') }}
        </p>
      </div>

      <p class="text-16 hidden md:block">
        {{ t('description') }}
      </p>

      <RoomieButton
        id="pd-bottom-cta-contact-constructor"
        data-lh-id="pd-bottom-cta-contact-constructor"
        type="button"
        :full-width="true"
        class="w-full md:max-w-md"
        @click="handleCtaClick">
        {{ t('cta') }}
      </RoomieButton>
    </div>
  </div>
</template>

<style lang="scss">
  .pd-fixed-ctas {
    @apply border-t border-carbon-light-50;

    .whatsapp-fixed-cta-label {
      display: none;
    }
  }
</style>

<i18n src="./i18n.json" lang="json"></i18n>
